// Grafieken voor de PDF-rapporten, als kale SVG-tekst.
//
// WAAROM NIET DE GRAFIEK VAN HET SCHERM KOPIËREN. Voor de hand liggend zou zijn
// om de Recharts-SVG uit de DOM te serialiseren. Dat werkt hier niet:
//   • de tabbladen van Sales blijven wel gemonteerd maar staan op `hidden`, en een
//     ResponsiveContainer met `display:none` meet 0 × 0 — je kopieert een leeg vlak;
//   • de kleuren van het scherm volgen het thema (donkere achtergrond, `var(--…)`),
//     terwijl een rapport altijd op wit papier komt;
//   • de legende van Recharts staat BUITEN de SVG, in een HTML-div.
// Daarom tekent deze module de grafiek zelf: uit dezelfde cijfers als de tabel
// eronder, deterministisch, en te testen zonder browser.
//
// Alles is pure tekst-in/tekst-uit. Geen DOM, geen React.

// XML-veilig maken. Een verkopersnaam met een & erin maakt de hele SVG anders
// ongeldig, en dan toont de PDF een leeg kader zonder foutmelding.
const xml = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Getallen afronden op 2 decimalen: SVG-coördinaten met 14 cijfers maken het
// bestand groot en de diff onleesbaar.
const c = (v) => Math.round(Number(v) * 100) / 100;

const nlGetal = (v, max = 0) => Number(v || 0).toLocaleString('nl-BE', { maximumFractionDigits: max });
// Percentages met ALTIJD één decimaal: "25 %" naast "24,7 %" in dezelfde legende
// leest als twee verschillende soorten getallen.
const nlPct = (v) => Number(v || 0).toLocaleString('nl-BE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

// ── Y-as: een "ronde" stapgrootte ──────────────────────────────────────────────
// 1, 2, 2,5 of 5 maal een macht van tien. Zonder dit staan er tickwaarden als
// 3.457 op de as en die leest niemand.
export function asStap(max, mikpunt = 5) {
  if (!(max > 0)) return 1;
  const ruw = max / Math.max(1, mikpunt);
  const macht = Math.pow(10, Math.floor(Math.log10(ruw)));
  const genormaliseerd = ruw / macht;
  const keuze = genormaliseerd <= 1 ? 1 : genormaliseerd <= 2 ? 2 : genormaliseerd <= 2.5 ? 2.5 : genormaliseerd <= 5 ? 5 : 10;
  return keuze * macht;
}

// De bovengrens van de as: het eerste veelvoud van de stap boven het maximum.
export function asTop(max, mikpunt = 5) {
  const stap = asStap(max, mikpunt);
  if (!(max > 0)) return stap;
  return Math.ceil(max / stap) * stap;
}

// ── Gestapelde staafgrafiek ─────────────────────────────────────────────────────
// `rijen`   : [{ label, waarden: { <reekssleutel>: getal } }]
// `reeksen` : [{ key, naam, kleur }] — onderaan eerst, dezelfde volgorde als op het scherm
//
// Geeft een SVG-string met een vaste viewBox, zodat hij in de PDF meeschaalt met
// de kolombreedte zonder te vervormen.
export function staafGrafiekSvg({ rijen = [], reeksen = [], breedte = 700, hoogte = 260, eenheid = '' } = {}) {
  const marge = { boven: 10, rechts: 8, onder: 46, links: 52 };
  const vlakB = breedte - marge.links - marge.rechts;
  const vlakH = hoogte - marge.boven - marge.onder;
  if (!rijen.length || !reeksen.length || vlakB <= 0 || vlakH <= 0) {
    return leegVlak(breedte, hoogte, 'Geen gegevens voor deze selectie.');
  }

  const totaalVan = (r) => reeksen.reduce((s, x) => s + (Number(r.waarden?.[x.key]) || 0), 0);
  const maxTotaal = Math.max(...rijen.map(totaalVan), 0);
  const top = asTop(maxTotaal);
  const stap = asStap(maxTotaal);
  const y = (waarde) => marge.boven + vlakH - (waarde / top) * vlakH;

  const stapB = vlakB / rijen.length;
  // Staven krijgen hooguit 34 px en houden altijd lucht tussen zich: bij twaalf
  // maanden zou een volle stapbreedte één aaneengesloten blok geven.
  const staafB = Math.max(4, Math.min(34, stapB * 0.62));

  const gridlijnen = [];
  const asLabels = [];
  for (let v = 0; v <= top + 1e-9; v += stap) {
    const yy = c(y(v));
    gridlijnen.push(`<line x1="${c(marge.links)}" y1="${yy}" x2="${c(breedte - marge.rechts)}" y2="${yy}" stroke="#e2e8f0" stroke-width="1"/>`);
    asLabels.push(`<text x="${c(marge.links - 6)}" y="${c(yy + 3)}" text-anchor="end" font-size="9" fill="#64748b">${xml(nlGetal(v))}</text>`);
  }

  // Bij meer dan twaalf tijdvakken schuin zetten, anders lopen de maandlabels in
  // elkaar. Onder de twaalf blijven ze recht — dat leest beter.
  const schuin = rijen.length > 12;
  const staven = [];
  const xLabels = [];
  rijen.forEach((r, i) => {
    const x = marge.links + i * stapB + (stapB - staafB) / 2;
    let bodem = 0;
    reeksen.forEach((s) => {
      const w = Number(r.waarden?.[s.key]) || 0;
      if (w <= 0) return;
      const y0 = y(bodem + w), y1 = y(bodem);
      staven.push(`<rect x="${c(x)}" y="${c(y0)}" width="${c(staafB)}" height="${c(Math.max(0.5, y1 - y0))}" fill="${xml(s.kleur)}"/>`);
      bodem += w;
    });
    const mx = c(x + staafB / 2);
    const my = c(marge.boven + vlakH + 12);
    xLabels.push(schuin
      ? `<text x="${mx}" y="${my}" font-size="8.5" fill="#64748b" text-anchor="end" transform="rotate(-45 ${mx} ${my})">${xml(r.label)}</text>`
      : `<text x="${mx}" y="${my}" font-size="8.5" fill="#64748b" text-anchor="middle">${xml(r.label)}</text>`);
  });

  const eenheidLabel = eenheid
    ? `<text x="${c(marge.links)}" y="${c(marge.boven - 1)}" font-size="8.5" fill="#94a3b8">${xml(eenheid)}</text>`
    : '';

  return `<svg viewBox="0 0 ${breedte} ${hoogte}" width="100%" height="${hoogte}" xmlns="http://www.w3.org/2000/svg" role="img">
${eenheidLabel}${gridlijnen.join('')}${staven.join('')}
<line x1="${c(marge.links)}" y1="${c(marge.boven + vlakH)}" x2="${c(breedte - marge.rechts)}" y2="${c(marge.boven + vlakH)}" stroke="#cbd5e1" stroke-width="1"/>
${asLabels.join('')}${xLabels.join('')}</svg>`;
}


// ── Meetgrafiek (Metering-rapport) ──────────────────────────────────────────────
// De grafiek "Verbruik (& productie)" zoals ze op het scherm staat: staven per
// tijdvak (verbruik en productie NAAST elkaar — het zijn twee stromen, geen
// stapel), daaroverheen optioneel de netto-lijn en de prijslijn op een tweede as
// rechts (EUR/MWh). Precies wat het scherm toont, komt in het rapport: de
// aanroeper geeft enkel de reeksen mee die daar aan staan.
//
// `rijen`     : [{ key, label, dof, waarden: { verbruik, productie, netto, prijs } }]
//               `label` = de tekst onder de as ('' = geen label, bv. een kwartier
//               dat geen heel uur is); `dof` = staaf doffer tekenen (vorig jaar).
// `staven`    : [{ key, naam, kleur }]  — verbruik/productie
// `lijnen`    : [{ key, naam, kleur, as: 'links'|'rechts', streep?: bool }]
// `grens`     : sleutel waar een stippellijn de jaargrens markeert (optioneel)
// `banden`  : [{ laag, hoog, kleur, opacity? }] — een zacht gekleurd vlak tussen twee
//             sleutels op de linkeras (laagste–hoogste per kwartier in het dagprofiel).
// `zones`   : [{ van, tot, kleur, opacity?, label? }] — een achtergrondvlak over de
//             rijen van sleutel `van` tot vóór sleutel `tot` (null = tot het einde),
//             zoals de dal- en piekuren in het dagprofiel van de marktprijzen.
// `asVanNul`: false laat de linkeras bij het minimum beginnen in plaats van bij 0
//             (termijnprijzen tussen 70 en 90 zouden anders een vlakke lijn worden).
export function meetGrafiekSvg({ rijen = [], staven = [], lijnen = [], banden = [], zones = [], breedte = 700, hoogte = 280, eenheid = 'kWh', eenheidRechts = 'EUR/MWh', grens = null, grensLabel = '', asVanNul = true } = {}) {
  const rechtsAan = lijnen.some((l) => l.as === 'rechts');
  const marge = { boven: 14, rechts: rechtsAan ? 50 : 10, onder: 40, links: 54 };
  const vlakB = breedte - marge.links - marge.rechts;
  const vlakH = hoogte - marge.boven - marge.onder;
  if (!rijen.length || (!staven.length && !lijnen.length) || vlakB <= 0 || vlakH <= 0) {
    return leegVlak(breedte, hoogte, 'Geen meetgegevens voor deze periode.');
  }
  // Ontbrekend (null/undefined/'') is géén 0: een lijn slaat het kwartier over
  // in plaats van naar de nullijn te duiken.
  const w = (r, k) => { const x = r.waarden?.[k]; if (x == null || x === '') return null; const v = Number(x); return Number.isFinite(v) ? v : null; };

  // Linkeras: staven, lijnen-links en banden samen; onder nul kan (netto bij veel productie).
  const linksSleutels = [...staven.map((s) => s.key), ...lijnen.filter((l) => l.as !== 'rechts').map((l) => l.key), ...banden.flatMap((b) => [b.laag, b.hoog])];
  let maxL = asVanNul ? 0 : -Infinity, minL = asVanNul ? 0 : Infinity;
  for (const r of rijen) for (const k of linksSleutels) { const v = w(r, k); if (v == null) continue; if (v > maxL) maxL = v; if (v < minL) minL = v; }
  if (!Number.isFinite(maxL)) { maxL = 0; minL = 0; }
  let topL, stapL, bodemL;
  if (asVanNul) {
    topL = asTop(maxL);
    stapL = asStap(maxL);
    bodemL = minL < 0 ? -asTop(-minL) : 0;
  } else {
    // As rond de waarden zelf: ronde stap op het bereik, bodem = het veelvoud
    // onder het minimum, top = het veelvoud boven het maximum.
    stapL = asStap(Math.max(maxL - minL, Math.abs(maxL) * 0.05, 1e-9));
    bodemL = Math.floor(minL / stapL) * stapL;
    topL = Math.ceil(maxL / stapL) * stapL;
    if (topL <= bodemL) topL = bodemL + stapL;
  }
  const yL = (v) => marge.boven + vlakH - ((v - bodemL) / (topL - bodemL)) * vlakH;

  // Rechteras (prijs): eigen schaal, mag onder nul (negatieve spotprijs).
  let maxR = 0, minR = 0;
  for (const r of rijen) for (const l of lijnen) { if (l.as !== 'rechts') continue; const v = w(r, l.key); if (v == null) continue; if (v > maxR) maxR = v; if (v < minR) minR = v; }
  const topR = asTop(maxR);
  const stapR = asStap(maxR);
  const bodemR = minR < 0 ? -asTop(-minR) : 0;
  const yR = (v) => marge.boven + vlakH - ((v - bodemR) / (topR - bodemR)) * vlakH;

  const stapB = vlakB / rijen.length;
  const x0 = (i) => marge.links + i * stapB;
  const xm = (i) => x0(i) + stapB / 2;

  const grid = [];
  const asLinks = [];
  for (let v = bodemL; v <= topL + 1e-9; v += stapL) {
    const yy = c(yL(v));
    grid.push(`<line x1="${c(marge.links)}" y1="${yy}" x2="${c(breedte - marge.rechts)}" y2="${yy}" stroke="${v === 0 ? '#cbd5e1' : '#e2e8f0'}" stroke-width="1"/>`);
    asLinks.push(`<text x="${c(marge.links - 6)}" y="${c(yy + 3)}" text-anchor="end" font-size="8.5" fill="#64748b">${xml(nlGetal(v))}</text>`);
  }
  const asRechts = [];
  if (rechtsAan) {
    for (let v = bodemR; v <= topR + 1e-9; v += stapR) {
      asRechts.push(`<text x="${c(breedte - marge.rechts + 6)}" y="${c(yR(v) + 3)}" text-anchor="start" font-size="8.5" fill="#64748b">${xml(nlGetal(v))}</text>`);
    }
  }

  // Staven: per tijdvak een groepje naast elkaar, hooguit 22 px per staaf.
  const nStaven = Math.max(1, staven.length);
  const groepB = Math.min(stapB * 0.72, 22 * nStaven + 2 * (nStaven - 1));
  const staafB = Math.max(1.2, (groepB - 2 * (nStaven - 1)) / nStaven);
  const rects = [];
  rijen.forEach((r, i) => {
    const start = xm(i) - groepB / 2;
    staven.forEach((s, j) => {
      const v = w(r, s.key);
      if (v == null || v === 0) return;
      const yTop = yL(Math.max(v, 0)), yBot = yL(Math.min(v, 0));
      rects.push(`<rect x="${c(start + j * (staafB + 2))}" y="${c(yTop)}" width="${c(staafB)}" height="${c(Math.max(0.5, yBot - yTop))}" fill="${xml(s.kleur)}"${r.dof ? ' fill-opacity="0.45"' : ''}/>`);
    });
  });

  // Zones: achtergrondvlakken over een reeks rijen, met het label linksboven.
  const zoneSvg = zones.map((z) => {
    const i0 = rijen.findIndex((r) => r.key === z.van);
    if (i0 < 0) return '';
    const i1 = z.tot == null ? rijen.length : rijen.findIndex((r) => r.key === z.tot);
    const einde = i1 < 0 ? rijen.length : i1;
    if (einde <= i0) return '';
    const x = c(x0(i0)), b = c(x0(einde) - x0(i0));
    return `<rect x="${x}" y="${c(marge.boven)}" width="${b}" height="${c(vlakH)}" fill="${xml(z.kleur)}" fill-opacity="${c(z.opacity == null ? 0.07 : z.opacity)}"/>`
      + (z.label ? `<text x="${c(x0(i0) + 3)}" y="${c(marge.boven + vlakH - 4)}" font-size="7.5" fill="#94a3b8">${xml(z.label)}</text>` : '');
  });

  // Banden: heen langs de bovengrens, terug langs de ondergrens; kwartieren zonder
  // waarde worden overgeslagen (zoals connectNulls op het scherm).
  const vlakken = banden.map((b) => {
    const boven = [], onder = [];
    rijen.forEach((r, i) => {
      const lo = w(r, b.laag), hi = w(r, b.hoog);
      if (lo == null || hi == null) return;
      boven.push(`${c(xm(i))},${c(yL(hi))}`); onder.push(`${c(xm(i))},${c(yL(lo))}`);
    });
    if (boven.length < 2) return '';
    return `<polygon points="${[...boven, ...onder.reverse()].join(' ')}" fill="${xml(b.kleur)}" fill-opacity="${c(b.opacity == null ? 0.16 : b.opacity)}" stroke="none"/>`;
  });

  // Lijnen: ontbrekende waarden onderbreken de lijn niet (zoals connectNulls).
  const paden = lijnen.map((l) => {
    const y = l.as === 'rechts' ? yR : yL;
    const punten = [];
    rijen.forEach((r, i) => { const v = w(r, l.key); if (v != null) punten.push(`${c(xm(i))},${c(y(v))}`); });
    if (punten.length < 2) return '';
    const d = punten.map((p, i) => `${i === 0 ? 'M' : 'L'}${p}`).join(' ');
    const halo = l.as === 'rechts' ? `<path d="${d}" fill="none" stroke="#ffffff" stroke-width="4" stroke-linejoin="round"/>` : '';
    return `${halo}<path d="${d}" fill="none" stroke="${xml(l.kleur)}" stroke-width="${l.as === 'rechts' ? 1.8 : 1.6}" stroke-linejoin="round"${l.streep ? ' stroke-dasharray="5 3"' : ''}/>`;
  });

  // Jaargrens: stippellijn met het jaar erbij, links van de eerste rij van dat jaar.
  let grensSvg = '';
  if (grens != null) {
    const gi = rijen.findIndex((r) => r.key === grens);
    if (gi >= 0) {
      const gx = c(x0(gi));
      grensSvg = `<line x1="${gx}" y1="${c(marge.boven)}" x2="${gx}" y2="${c(marge.boven + vlakH)}" stroke="#94a3b8" stroke-dasharray="4 3"/>`
        + (grensLabel ? `<text x="${c(x0(gi) + 4)}" y="${c(marge.boven + 9)}" font-size="8.5" fill="#94a3b8">${xml(grensLabel)}</text>` : '');
    }
  }

  // X-labels: enkel rijen met een label, schuin zodra het er veel zijn.
  const metLabel = rijen.filter((r) => r.label);
  const schuin = metLabel.length > 16;
  const xLabels = [];
  rijen.forEach((r, i) => {
    if (!r.label) return;
    const mx = c(xm(i)), my = c(marge.boven + vlakH + 12);
    xLabels.push(schuin
      ? `<text x="${mx}" y="${my}" font-size="8" fill="#64748b" text-anchor="end" transform="rotate(-45 ${mx} ${my})">${xml(r.label)}</text>`
      : `<text x="${mx}" y="${my}" font-size="8.5" fill="#64748b" text-anchor="middle">${xml(r.label)}</text>`);
  });

  const eenheidLinks = `<text x="${c(marge.links)}" y="${c(marge.boven - 4)}" font-size="8.5" fill="#94a3b8">${xml(eenheid)}</text>`;
  const eenheidRechtsSvg = rechtsAan ? `<text x="${c(breedte - marge.rechts)}" y="${c(marge.boven - 4)}" font-size="8.5" fill="#0891b2" text-anchor="end">${xml(eenheidRechts)}</text>` : '';

  return `<svg viewBox="0 0 ${breedte} ${hoogte}" width="100%" height="${hoogte}" xmlns="http://www.w3.org/2000/svg" role="img">
${eenheidLinks}${eenheidRechtsSvg}${zoneSvg.join('')}${grid.join('')}${grensSvg}${vlakken.join('')}${rects.join('')}${paden.join('')}
<line x1="${c(marge.links)}" y1="${c(marge.boven + vlakH)}" x2="${c(breedte - marge.rechts)}" y2="${c(marge.boven + vlakH)}" stroke="#cbd5e1" stroke-width="1"/>
${asLinks.join('')}${asRechts.join('')}${xLabels.join('')}</svg>`;
}
// ── Donut ───────────────────────────────────────────────────────────────────────
// `segmenten`: [{ naam, waarde, kleur }]. In het midden komt het totaal te staan.
export function donutSvg({ segmenten = [], grootte = 150, dikte = 26, middenLabel = '' } = {}) {
  const lijst = (segmenten || []).filter((s) => Number(s.waarde) > 0);
  const totaal = lijst.reduce((s, x) => s + Number(x.waarde), 0);
  if (!totaal) return leegVlak(grootte, grootte, 'Geen data');

  const m = grootte / 2;
  const buiten = m - 2;
  const binnen = Math.max(4, buiten - dikte);

  // Eén segment van 100%: begin- en eindpunt van de boog vallen samen, en dan
  // tekent een arc-pad NIETS. Dat is de klassieke donut-bug — vandaar twee ringen
  // in plaats van een boog.
  const stukken = lijst.length === 1
    ? [`<circle cx="${c(m)}" cy="${c(m)}" r="${c((buiten + binnen) / 2)}" fill="none" stroke="${xml(lijst[0].kleur)}" stroke-width="${c(buiten - binnen)}"/>`]
    : lijst.map((s) => {
      const deel = Number(s.waarde) / totaal;
      const pad = ringPad(m, buiten, binnen, hoekVan(lijst, s), hoekVan(lijst, s) + deel * 360);
      return `<path d="${pad}" fill="${xml(s.kleur)}"/>`;
    });

  const midden = middenLabel || nlGetal(totaal);
  return `<svg viewBox="0 0 ${grootte} ${grootte}" width="${grootte}" height="${grootte}" xmlns="http://www.w3.org/2000/svg" role="img">
${stukken.join('')}
<text x="${c(m)}" y="${c(m + 1)}" text-anchor="middle" font-size="15" font-weight="700" fill="#0f172a">${xml(midden)}</text>
<text x="${c(m)}" y="${c(m + 13)}" text-anchor="middle" font-size="8" fill="#64748b">totaal</text></svg>`;
}

// Startgraden van een segment: de som van alles wat ervóór komt. −90 zodat de
// eerste taartpunt bovenaan begint en niet rechts.
function hoekVan(lijst, doel) {
  const totaal = lijst.reduce((s, x) => s + Number(x.waarde), 0);
  let graden = -90;
  for (const s of lijst) {
    if (s === doel) break;
    graden += (Number(s.waarde) / totaal) * 360;
  }
  return graden;
}

function punt(cx, cy, r, graden) {
  const rad = (graden * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

// Ringsegment: buitenboog heen, binnenboog terug.
export function ringPad(m, buiten, binnen, vanGraden, totGraden) {
  const groot = totGraden - vanGraden > 180 ? 1 : 0;
  const [x1, y1] = punt(m, m, buiten, vanGraden);
  const [x2, y2] = punt(m, m, buiten, totGraden);
  const [x3, y3] = punt(m, m, binnen, totGraden);
  const [x4, y4] = punt(m, m, binnen, vanGraden);
  return `M ${c(x1)} ${c(y1)} A ${c(buiten)} ${c(buiten)} 0 ${groot} 1 ${c(x2)} ${c(y2)}`
    + ` L ${c(x3)} ${c(y3)} A ${c(binnen)} ${c(binnen)} 0 ${groot} 0 ${c(x4)} ${c(y4)} Z`;
}

function leegVlak(breedte, hoogte, tekst) {
  return `<svg viewBox="0 0 ${breedte} ${hoogte}" width="100%" height="${hoogte}" xmlns="http://www.w3.org/2000/svg" role="img">
<rect x="0.5" y="0.5" width="${breedte - 1}" height="${hoogte - 1}" fill="#f8fafc" stroke="#e2e8f0"/>
<text x="${c(breedte / 2)}" y="${c(hoogte / 2)}" text-anchor="middle" font-size="10" fill="#94a3b8">${xml(tekst)}</text></svg>`;
}

// Legende bij een grafiek, als HTML — kleurvlakje, naam, waarde en aandeel.
// Hoort NIET in de SVG: in HTML loopt hij netjes door over meerdere regels en
// blijft hij selecteerbare tekst in de PDF.
export function legendeHtml(items, { toonAandeel = true, formatteer = (v) => nlGetal(v) } = {}) {
  const lijst = items || [];
  const totaal = lijst.reduce((s, x) => s + (Number(x.waarde) || 0), 0);
  return lijst.map((i) => {
    const aandeel = toonAandeel && totaal > 0 ? ` <span class="lg-pct">${nlPct((Number(i.waarde) / totaal) * 100)}%</span>` : '';
    return `<div class="lg-item"><span class="lg-stip" style="background:${xml(i.kleur)}"></span>`
      + `<span class="lg-naam">${xml(i.naam)}</span>`
      + `<span class="lg-waarde">${xml(formatteer(i.waarde))}</span>${aandeel}</div>`;
  }).join('');
}
